import { useState } from "react";
import { getCard, getCase } from "../../content/lookups.js";
import { fatigueLimit } from "../../domain/run/fatigueLimit.js";
import { useGame } from "../../app/GameProvider.js";
import { CardView } from "../../components/CardView.js";
import { ChoiceHead } from "../shared/ChoiceHead.js";
import { rewardCommand, rewardConfirmLabel, rewardRecoverLabel, type RewardChoice } from "./rewardChoice.js";

export function RewardCardScreen() {
  const { state, dispatch } = useGame();
  const [choice, setChoice] = useState<RewardChoice | null>(null);
  const [rewardPage, setRewardPage] = useState(0);
  const run = state.run;
  if (!run) return null;

  const rewardIds = run.rewardCardIds.length
    ? run.rewardCardIds
    : (["mid", "village", "cellar"] as const);
  const recoverLabel = rewardRecoverLabel(getCase(run.activeCase?.caseId ?? "")?.size);
  const selectedName = choice?.kind === "card" ? getCard(choice.cardId)?.name : undefined;
  const visibleCardId = rewardIds[rewardPage] ?? rewardIds[0];
  const carouselCards: { cardId: (typeof rewardIds)[number]; position: "previous" | "current" | "next" }[] = rewardIds.length < 2
    ? [{ cardId: visibleCardId, position: "current" }]
    : rewardIds.length === 2
      ? [
        { cardId: visibleCardId, position: "current" },
        { cardId: rewardIds[(rewardPage + 1) % rewardIds.length], position: "next" },
      ]
      : [
        { cardId: rewardIds[(rewardPage - 1 + rewardIds.length) % rewardIds.length], position: "previous" },
        { cardId: visibleCardId, position: "current" },
        { cardId: rewardIds[(rewardPage + 1) % rewardIds.length], position: "next" },
      ];

  const showPrevious = () => setRewardPage((page) => (page - 1 + rewardIds.length) % rewardIds.length);
  const showNext = () => setRewardPage((page) => (page + 1) % rewardIds.length);

  return (
    <section className="screen screen-reward">
      <ChoiceHead kicker="REWARD" title="報酬を選ぶ" />
      <p className="reward-copy">カードを1枚取るか、疲労を回復する。</p>
      <p className="reward-fatigue">
        全社疲労 <b>{run.fatigue}</b> / {fatigueLimit(run.organizations)}
      </p>
      <div className="reward-carousel" aria-label="獲得候補">
        <button
          type="button"
          className="reward-carousel-button"
          aria-label="前の候補を表示"
          disabled={rewardIds.length < 2}
          onClick={showPrevious}
        >
          ‹
        </button>
        <div className="reward-card-stage">
          {carouselCards.map(({ cardId, position }) => {
            const def = getCard(cardId);
            if (!def) return null;
            const isCurrent = position === "current";
            const selected = isCurrent && choice?.kind === "card" && choice.cardId === cardId;
            return (
              <CardView
                key={`${position}-${cardId}`}
                card={{ instanceId: `reward-${position}-${cardId}`, cardId }}
                definition={def}
                selectable
                picked={selected}
                className={`reward-card is-${position}${selected ? " is-selected" : ""}`}
                onClick={() => isCurrent ? setChoice({ kind: "card", cardId }) : setRewardPage(rewardIds.findIndex((id) => id === cardId))}
              />
            );
          })}
        </div>
        <button
          type="button"
          className="reward-carousel-button"
          aria-label="次の候補を表示"
          disabled={rewardIds.length < 2}
          onClick={showNext}
        >
          ›
        </button>
      </div>
      <p className="reward-carousel-page" aria-live="polite">{rewardPage + 1} / {rewardIds.length}</p>
      <button
        type="button"
        className={`reward-skip${choice?.kind === "recover" ? " is-selected" : ""}`}
        aria-pressed={choice?.kind === "recover"}
        onClick={() => setChoice({ kind: "recover" })}
      >
        <small>カードを取らない</small>
        <strong>{recoverLabel}</strong>
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={!choice}
        onClick={() => choice && dispatch(rewardCommand(choice))}
      >
        {rewardConfirmLabel(choice, selectedName, recoverLabel)}
      </button>
    </section>
  );
}
